import { isoWeekdayOfName, mergeOpeningHoursFill, openingHoursKeyOf, parseOpeningHoursFill } from "./opening-hours-fill.js";
/** Why a raw value is not a value of its kind. Recorded on the refusal; never shown to the model as more than "invalid". */
export const FILL_VALUE_PROBLEM = [
    "wrong_type",
    "empty",
    "too_long",
    "control_character",
    "not_an_option",
    "not_a_time",
    "not_a_date",
    "not_a_whole_number",
    "out_of_range",
    "repeated",
    /** AMENDED 2026-10-18 (the opening-hours fill): a time zone that is not an IANA zone name's shape. */
    "not_a_time_zone",
];
/** A single-line text value's ceiling when its field names none (the protocol's page context caps a frame anyway). */
export const FILL_TEXT_CHAR_LIMIT = 500;
/** A multi-choice value holds at most this many choices. */
export const FILL_CHOICE_COUNT_LIMIT = 32;
/** A choice's code: a language code, a minute count, an attempt number — never free text. */
const CHOICE = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;
/** C0 and C1 controls, line and paragraph separators: a fillable text is one line the person could have typed. */
// eslint-disable-next-line no-control-regex -- the point of the pattern is to find control characters
const CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
const LOCAL_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const refused = (reason) => ({ ok: false, reason });
function parseText(raw) {
    if (typeof raw !== "string")
        return refused("wrong_type");
    const value = raw.trim();
    if (value === "")
        return refused("empty");
    if (CONTROL.test(value))
        return refused("control_character");
    if (value.length > FILL_TEXT_CHAR_LIMIT)
        return refused("too_long");
    return { ok: true, value };
}
/** A choice code; a whole number is read as its decimal code (the model may send `1440` for "1440"). */
function parseChoice(raw) {
    const value = typeof raw === "number" && Number.isSafeInteger(raw) && raw >= 0 ? String(raw) : raw;
    if (typeof value !== "string")
        return refused("wrong_type");
    if (value === "")
        return refused("empty");
    return CHOICE.test(value) ? { ok: true, value } : refused("not_an_option");
}
function parseChoiceSet(raw) {
    if (!Array.isArray(raw))
        return refused("wrong_type");
    if (raw.length === 0)
        return refused("empty");
    if (raw.length > FILL_CHOICE_COUNT_LIMIT)
        return refused("too_long");
    const value = [];
    for (const one of raw) {
        const choice = parseChoice(one);
        if (!choice.ok)
            return choice;
        if (value.includes(choice.value))
            return refused("repeated");
        value.push(choice.value);
    }
    return { ok: true, value: Object.freeze(value) };
}
function parseLocalTime(raw) {
    if (typeof raw !== "string")
        return refused("wrong_type");
    return LOCAL_TIME.test(raw) ? { ok: true, value: raw } : refused("not_a_time");
}
/** An ISO calendar date that exists (`2026-02-30` is refused); the form converts it to its own display. */
function parseLocalDate(raw) {
    if (typeof raw !== "string")
        return refused("wrong_type");
    const match = LOCAL_DATE.exec(raw);
    if (match === null)
        return refused("not_a_date");
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    const exists = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
    return exists ? { ok: true, value: raw } : refused("not_a_date");
}
function parseWholeNumber(raw) {
    if (typeof raw !== "number")
        return refused("wrong_type");
    if (!Number.isSafeInteger(raw))
        return refused("not_a_whole_number");
    return raw < 0 ? refused("out_of_range") : { ok: true, value: raw };
}
/**
 * One ISO day written as a single digit 1–7 in a string → that number (EVAL-RUN round 2, 2026-10-16: on the pool the
 * model sent ["1", "2", "3", "4", "5"] for weekdays in both languages, the right days as JSON strings). Exactly one
 * ASCII digit, nothing around it: "01", " 1", "1.0" and "8" stay text, so they are refused as before.
 */
function isoDayOfDigit(text) {
    return /^[1-7]$/u.test(text) ? Number(text) : undefined;
}
/**
 * ISO days of the week, 1 (Monday) … 7 (Sunday), as the campaign form's day chips hold them; sorted. A weekday's whole
 * name (English or Greek), or its ISO number as a one-digit string, is read as its number; the value is always numbers.
 */
function parseDaySet(raw) {
    if (!Array.isArray(raw))
        return refused("wrong_type");
    if (raw.length === 0)
        return refused("empty");
    const value = [];
    for (const each of raw) {
        const one = typeof each === "string" ? (isoWeekdayOfName(each) ?? isoDayOfDigit(each) ?? each) : each;
        if (typeof one !== "number")
            return refused("wrong_type");
        if (!Number.isInteger(one) || one < 1 || one > 7)
            return refused("out_of_range");
        if (value.includes(one))
            return refused("repeated");
        value.push(one);
    }
    return { ok: true, value: Object.freeze(value.sort((left, right) => left - right)) };
}
/** A yes/no control (a checkbox, a switch): a boolean; "on"/"off" and "yes"/"no" are read as it (the model's words). */
function parseToggle(raw) {
    if (typeof raw === "boolean")
        return { ok: true, value: raw };
    if (typeof raw === "string") {
        const word = raw.trim().toLowerCase();
        if (word === "on" || word === "yes" || word === "true")
            return { ok: true, value: true };
        if (word === "off" || word === "no" || word === "false")
            return { ok: true, value: false };
    }
    return refused("wrong_type");
}
const PARSER = {
    text: parseText,
    single_choice: parseChoice,
    multi_choice: parseChoiceSet,
    local_time: parseLocalTime,
    local_date: parseLocalDate,
    whole_number: parseWholeNumber,
    day_set: parseDaySet,
    toggle: parseToggle,
    opening_hours: parseOpeningHoursFill,
};
/** A raw proposed value as a value of `kind`, or why not. Total over `unknown`; never throws. */
export function parseFillValue(kind, raw) {
    const parse = PARSER[kind];
    return parse(raw);
}
/**
 * A raw value for one registered field: its kind's parse, then the field's own static bounds (`maxLength`, `option`).
 * Dynamic bounds (what the form offers right now) are the page binding's `accepts`.
 */
export function parseFieldValue(field, raw) {
    const parsed = parseFillValue(field.kind, raw);
    if (!parsed.ok)
        return parsed;
    const value = parsed.value;
    if (field.maxLength !== undefined && typeof value === "string" && value.length > field.maxLength)
        return refused("too_long");
    if (field.option !== undefined) {
        const allowed = field.option;
        const chosen = Array.isArray(value) ? value : [value];
        if (!chosen.every((one) => typeof one === "string" && allowed.includes(one)))
            return refused("not_an_option");
    }
    return parsed;
}
/**
 * The value a fill leaves in a field: the proposed value itself, except for `opening_hours`, whose proposal is a change
 * set merged into the editor's whole state (`./opening-hours-fill.ts`). `before` is the field's value now (`null`: none).
 */
export function mergedFillValue(kind, before, change) {
    if (kind !== "opening_hours")
        return change;
    const held = parseOpeningHoursFill(before);
    return mergeOpeningHoursFill(held.ok ? held.value : null, change);
}
/** Whether two fill values say the same thing: scalars by value, flat arrays element by element, opening hours by key. */
export function sameFillValue(left, right) {
    if (Array.isArray(left) && Array.isArray(right))
        return left.length === right.length && left.every((one, index) => one === right[index]);
    if (typeof left === "object" && left !== null && typeof right === "object" && right !== null && !Array.isArray(left) && !Array.isArray(right)) {
        const one = parseOpeningHoursFill(left);
        const other = parseOpeningHoursFill(right);
        return one.ok && other.ok && openingHoursKeyOf(one.value) === openingHoursKeyOf(other.value);
    }
    return left === right;
}
//# sourceMappingURL=fill-value.js.map